import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Stack, Button, Alert, Skeleton,
} from '@mui/material';
import {
  People, Forum, Whatshot, PersonAdd, TrendingUp,
} from '@mui/icons-material';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { api } from '../config/api';
import { useAuth } from '../context/AuthContext';

interface Insights {
  resumen: { hoy: { atendidos: number; mensajes: number; interesados: number; leads: number }; totales: { conversaciones: number; leads: number } };
  serie: Array<{ dia: string; mensajes: number; leads: number }>;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<Insights | null>(null);
  const [metricas, setMetricas] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/insights').then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/soporte/metricas').then((r) => setMetricas(r.data)).catch(() => {});
  }, []);

  const hoy = data?.resumen.hoy;

  const kpis = [
    { label: 'Atendidos hoy', value: hoy?.atendidos ?? 0, icon: <People />, color: '#3B82F6' },
    { label: 'Mensajes hoy', value: hoy?.mensajes ?? 0, icon: <Forum />, color: '#25D366' },
    { label: 'Interesados hoy', value: hoy?.interesados ?? 0, icon: <Whatshot />, color: '#F59E0B' },
    { label: 'Leads hoy', value: hoy?.leads ?? 0, icon: <PersonAdd />, color: '#8B5CF6' },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Hola, {user?.nombre?.split(' ')[0]} 👋</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {loading ? 'Cargando tu resumen...' : hoy && hoy.atendidos > 0
          ? `Hoy tu agente atendió a ${hoy.atendidos} ${hoy.atendidos === 1 ? 'persona' : 'personas'}, de las cuales ${hoy.interesados} ${hoy.interesados === 1 ? 'está interesada' : 'están interesadas'}.`
          : 'Aún no hay actividad hoy. Activa tu agente y conecta tu WhatsApp para empezar.'}
      </Typography>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((k) => (
          <Grid item xs={6} md={3} key={k.label}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary">{k.label}</Typography>
                    {loading ? <Skeleton width={40} height={40} /> : <Typography variant="h4">{k.value}</Typography>}
                  </Box>
                  <Box sx={{ color: k.color, bgcolor: `${k.color}18`, p: 1.2, borderRadius: 2, display: 'flex' }}>{k.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Métricas de valor (estilo Wizybot) */}
      {metricas && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg,#25D366,#128C7E)', color: '#fff' }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>Ventas generadas</Typography>
                <Typography variant="h5">${(metricas.ventas?.total || 0).toLocaleString('es-CL')}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>{metricas.ventas?.cantidad || 0} ventas</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card><CardContent>
              <Typography variant="body2" color="text.secondary">Tiempo ahorrado</Typography>
              <Typography variant="h5">{Math.round((metricas.tiempoAhorradoMin || 0) / 60)}h {(metricas.tiempoAhorradoMin || 0) % 60}m</Typography>
              <Typography variant="caption" color="text.secondary">por respuestas del bot</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card><CardContent>
              <Typography variant="body2" color="text.secondary">Mensajes con IA</Typography>
              <Typography variant="h5">{metricas.mensajesIA || 0}</Typography>
              <Typography variant="caption" color="text.secondary">respondidos automáticamente</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card><CardContent>
              <Typography variant="body2" color="text.secondary">Tickets</Typography>
              <Typography variant="h5">{metricas.tickets?.abiertos || 0} <Typography component="span" variant="body2" color="text.secondary">abiertos</Typography></Typography>
              <Typography variant="caption" color="text.secondary">{metricas.tickets?.cerrados || 0} cerrados</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2}>
        {/* Gráfico */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <TrendingUp color="primary" />
                <Typography variant="h6">Actividad últimos 7 días</Typography>
              </Stack>
              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data?.serie || []}>
                    <defs>
                      <linearGradient id="gMsg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#25D366" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#25D366" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gLead" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="dia" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="mensajes" stroke="#25D366" fill="url(#gMsg)" name="Mensajes" />
                    <Area type="monotone" dataKey="leads" stroke="#8B5CF6" fill="url(#gLead)" name="Leads" />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Totales */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Totales</Typography>
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Box><Typography variant="h5">{data?.resumen.totales.conversaciones ?? 0}</Typography><Typography variant="caption" color="text.secondary">Conversaciones</Typography></Box>
                <Box><Typography variant="h5">{data?.resumen.totales.leads ?? 0}</Typography><Typography variant="caption" color="text.secondary">Leads totales</Typography></Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
